import React, { useCallback, useRef, useState } from "react";
import { message, Modal } from "antd";
import ReactCrop, { Crop, PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useTranslation } from "react-i18next";

export type CropShape = "rectangle" | "square" | null;

interface ResizableCropModalProps {
  open: boolean;
  imageUrl: string;
  onCancel: () => void;
  onOk: (croppedImageBlob: Blob) => void;
  /**
   * "rectangle" → 16:9 aspect ratio, locked (move only).
   * "square" → 1:1 aspect ratio, locked (move only).
   * null/undefined → any aspect ratio, free crop (resize from all sides).
   */
  shape?: CropShape;
}

const ASPECT_RATIO = {
  rectangle: 16 / 9,
  square: 1,
} as const;

const ResizableCropModal: React.FC<ResizableCropModalProps> = ({
  open,
  imageUrl,
  onCancel,
  onOk,
  shape,
}) => {
  const hasFixedAspect = shape === "rectangle" || shape === "square";
  const fixedAspect = hasFixedAspect && shape ? ASPECT_RATIO[shape] : undefined;
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>({
    unit: "%",
    width: 50,
    height: 50,
    x: 25,
    y: 25,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { width, height } = e.currentTarget;

      let cropW: number;
      let cropH: number;

      if (hasFixedAspect && fixedAspect != null) {
        // Fixed size: largest crop with aspect ratio that fits inside image
        if (width / height > fixedAspect) {
          cropH = height;
          cropW = height * fixedAspect;
        } else {
          cropW = width;
          cropH = width / fixedAspect;
        }
      } else {
        cropW = Math.min(width, height) * 0.7;
        cropH = cropW;
        cropW = Math.min(cropW, width);
        cropH = Math.min(cropH, height);
      }

      const cropX = (width - cropW) / 2;
      const cropY = (height - cropH) / 2;

      setCrop({
        unit: "px",
        width: cropW,
        height: cropH,
        x: cropX,
        y: cropY,
      });
    },
    [hasFixedAspect, fixedAspect],
  );

  const getCroppedImg = useCallback(
    async (image: HTMLImageElement, crop: PixelCrop): Promise<Blob | null> => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        return null;
      }

      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      const srcX = crop.x * scaleX;
      const srcY = crop.y * scaleY;
      const srcW = crop.width * scaleX;
      const srcH = crop.height * scaleY;

      canvas.width = srcW;
      canvas.height = srcH;
      ctx.drawImage(image, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);

      return new Promise<Blob | null>((resolve, reject) => {
        try {
          canvas.toBlob(
            (blob) => {
              if (blob) resolve(blob);
              else reject(new Error("Tainted canvas"));
            },
            "image/jpeg",
            0.95,
          );
        } catch (e) {
          reject(e);
        }
      });
    },
    [],
  );

  const handleCrop = async () => {
    if (!imgRef.current || !completedCrop) return;

    try {
      const croppedBlob = await getCroppedImg(imgRef.current, completedCrop);
      if (croppedBlob) {
        onOk(croppedBlob);
      }
    } catch {
      messageApi.warning(
        t("message.warning.crop_edit_upload") ||
          "Cannot edit this image from the server. Re-upload the image to crop it.",
      );
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        open={open}
        title={t("label.edit_image")}
        onCancel={onCancel}
        onOk={handleCrop}
        okText={t("button.done")}
        cancelText={t("button.cancel")}
        width="90%"
        style={{ maxWidth: "800px" }}
        styles={{
          body: {
            padding: 16,
            maxHeight: "min(70vh, 760px)",
            overflow: "hidden",
          },
        }}
        destroyOnHidden
      >
        <div
          style={{
            width: "100%",
            height: "min(55vh, 520px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#f0f0f0",
            padding: 12,
            overflow: "hidden",
            borderRadius: 8,
          }}
        >
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={fixedAspect}
            locked={hasFixedAspect}
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              ref={imgRef}
              src={imageUrl}
              alt="Crop"
              onLoad={onImageLoad}
              style={{
                display: "block",
                maxWidth: "100%",
                maxHeight: "100%",
                width: "100%",
                height: "auto",
                objectFit: "contain",
                verticalAlign: "middle",
              }}
            />
          </ReactCrop>
        </div>
        <div
          style={{
            marginTop: "16px",
            padding: "12px",
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
            textAlign: "center",
            color: "#666",
          }}
        >
          {hasFixedAspect
            ? t("label.crop_instructions_fixed") ||
              "Crop size is fixed. Drag the crop area to move it."
            : t("label.crop_instructions") ||
              "Drag the corners or edges to resize. Drag the center to move it."}
        </div>
      </Modal>
    </>
  );
};

export default ResizableCropModal;

